import React, { useMemo } from "react";

import { useGetCarbonProgramByFarmId, useNavigate, useTranslation, useUserContext } from "utils";

import { Flex, LazyLoadImage, Skeleton } from "components";

enum ONBOARDING_BANNER_TYPE {
  NON_ENROLLED_WITHOUT_FIELDS = "nonEnrolledWithoutFields",
  NON_ENROLLED_WITH_FIELDS = "nonEnrolledWithFields",
  ENROLLED_WITHOUT_FIELDS = "enrolledWithoutFields",
  ENROLLED_WITH_FIELDS = "enrolledWithFields",
  ENROLLED_WITH_FIELDS_AND_FOOTPRINTING = "enrolledWithFieldsAndFootprinting",
  DISABLED = "disabled",
}

const OnboardingBanner: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { user } = useUserContext();

  const { data: carbonProgram, isLoading: isLoadingCarbonProgram } = useGetCarbonProgramByFarmId(user?.farm.id ?? undefined);

  const farmHasFields = !!user?.farm?.fields && user?.farm?.fields > 0;
  const farmIsEnrolled = !!carbonProgram && carbonProgram.isEnabled !== false;
  const showAddFieldsButton = carbonProgram?.platformFeatures?.fieldManagement?.removalsExperience ?? true;
  const farmHasFootprintingEnabled = carbonProgram?.platformFeatures?.footprinting?.enabled && carbonProgram?.farmLevelFootprinting?.startYear;

  const bannerType = useMemo(() => {
    if (carbonProgram?.isEnabled === false) {
      return ONBOARDING_BANNER_TYPE.DISABLED;
    }
    if (farmIsEnrolled && farmHasFields && farmHasFootprintingEnabled) {
      return ONBOARDING_BANNER_TYPE.ENROLLED_WITH_FIELDS_AND_FOOTPRINTING;
    }
    if (farmIsEnrolled && farmHasFields) {
      return ONBOARDING_BANNER_TYPE.ENROLLED_WITH_FIELDS;
    }
    if (farmIsEnrolled && !farmHasFields) {
      return ONBOARDING_BANNER_TYPE.ENROLLED_WITHOUT_FIELDS;
    }
    if (!farmIsEnrolled && farmHasFields) {
      return ONBOARDING_BANNER_TYPE.NON_ENROLLED_WITH_FIELDS;
    }
    if (!farmIsEnrolled && !farmHasFields) {
      return ONBOARDING_BANNER_TYPE.NON_ENROLLED_WITHOUT_FIELDS;
    }
    return ONBOARDING_BANNER_TYPE.NON_ENROLLED_WITHOUT_FIELDS;
  }, [carbonProgram?.isEnabled, farmIsEnrolled, farmHasFields, farmHasFootprintingEnabled]);

  return !user || isLoadingCarbonProgram ? (
    <Skeleton className="onboarding-banner-container-skeleton" type="block" height="20rem" />
  ) : (
    <Flex justify="space-between" className="onboarding-banner-container" gap="2rem">
      <LazyLoadImage
        wrapperClassName="onboarding-banner-image"
        src={carbonProgram?.bannerImageUrl ?? "/images/homepage/homepage-banner.jpg"}
        alt="carbon-program-banner"
      />
      <Flex vertical justify="space-between" className="onboarding-banner-text" gap="1.5rem">
        <h1>{t(`onboardingBanner.${bannerType}.title`)}</h1>
        <p>{t(`onboardingBanner.${bannerType}.body`)}</p>
      </Flex>
      {bannerType !== ONBOARDING_BANNER_TYPE.DISABLED && (
        <Flex vertical justify="flex-end" className="onboarding-banner-button-container" gap="1rem">
          {showAddFieldsButton ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={t(`onboardingBanner.${bannerType}.CTAs.primary`)}
              className="onboarding-button-primary"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/all-fields");
                }
              }}
              onClick={() => navigate("/all-fields")}
            >
              {t(`onboardingBanner.${bannerType}.CTAs.primary`)}
            </span>
          ) : null}
          {farmHasFootprintingEnabled ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={t("onboardingBanner.enrolledWithFieldsAndFootprinting.CTAs.secondary")}
              className="onboarding-button-secondary"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/footprinting");
                }
              }}
              onClick={() => navigate("/footprinting")}
            >
              {t("onboardingBanner.enrolledWithFieldsAndFootprinting.CTAs.secondary")}
            </span>
          ) : null}
        </Flex>
      )}
    </Flex>
  );
};

export default OnboardingBanner;
